import { collection, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { debtService } from '../../debt-management/services/debtService';
import { paymentService } from '../../debt-management/services/paymentService';
import { savingsService } from '../../savings/services/savingsService';

export const backupService = {
    // Export dữ liệu ra JSON (bao gồm tiết kiệm)
    async exportData(userId) {
        try {
            const data = {
                debts: [],
                payments: [],
                savings: [],
                savingsTransactions: [],
                exportDate: new Date().toISOString(),
                userId: userId,
                version: '2.0' // Tăng version để hỗ trợ tiết kiệm
            };

            // Export debts
            const debts = await debtService.getDebts(userId);
            data.debts = debts.map(debt => ({
                ...debt,
                id: undefined, // Remove ID for import
                ngayVay: debt.ngayVay?.toISOString(),
                ngayTra: debt.ngayTra?.toISOString(),
                createdAt: debt.createdAt?.toISOString(),
                updatedAt: debt.updatedAt?.toISOString()
            }));

            // Export payments
            const payments = await paymentService.getPayments(userId);
            data.payments = payments.map(payment => ({
                ...payment,
                id: undefined, // Remove ID for import
                ngayTra: payment.ngayTra?.toISOString(),
                createdAt: payment.createdAt?.toISOString()
            }));

            // Export savings
            const savings = await savingsService.getSavings(userId);
            data.savings = savings.map(saving => ({
                ...saving,
                id: undefined, // Remove ID for import
                ngayTao: saving.ngayTao?.toISOString(),
                ngayDaoHan: saving.ngayDaoHan?.toISOString(),
                ngayCapNhat: saving.ngayCapNhat?.toISOString()
            }));

            // Export savings transactions
            const savingsTransactions = await savingsService.getSavingsTransactions(userId);
            data.savingsTransactions = savingsTransactions.map(transaction => ({
                ...transaction,
                id: undefined, // Remove ID for import
                ngayGiaoDich: transaction.ngayGiaoDich?.toISOString()
            }));

            return data;
        } catch (error) {
            console.error('Error exporting data:', error);
            throw error;
        }
    },

    // Import dữ liệu từ JSON (bao gồm tiết kiệm)
    async importData(userId, data) {
        try {
            // Validate data structure
            if (!data.debts || !data.payments) {
                throw new Error('Invalid data format - missing debts or payments');
            }

            // Import debts
            for (const debt of data.debts) {
                const debtData = {
                    ...debt,
                    ngayVay: new Date(debt.ngayVay),
                    ngayTra: new Date(debt.ngayTra),
                    createdAt: debt.createdAt ? new Date(debt.createdAt) : new Date(),
                    updatedAt: debt.updatedAt ? new Date(debt.updatedAt) : new Date(),
                    importedAt: new Date()
                };

                await debtService.addDebt(userId, debtData);
            }

            // Import payments
            for (const payment of data.payments) {
                const paymentData = {
                    ...payment,
                    ngayTra: new Date(payment.ngayTra),
                    createdAt: payment.createdAt ? new Date(payment.createdAt) : new Date(),
                    importedAt: new Date()
                };

                await paymentService.addPayment(userId, paymentData);
            }

            // Import savings (nếu có)
            if (data.savings && Array.isArray(data.savings)) {
                for (const saving of data.savings) {
                    const savingData = {
                        ...saving,
                        ngayTao: new Date(saving.ngayTao),
                        ngayDaoHan: new Date(saving.ngayDaoHan),
                        ngayCapNhat: saving.ngayCapNhat ? new Date(saving.ngayCapNhat) : new Date(),
                        importedAt: new Date()
                    };

                    await savingsService.addSavings(userId, savingData);
                }
            }

            // Import savings transactions (nếu có)
            if (data.savingsTransactions && Array.isArray(data.savingsTransactions)) {
                for (const transaction of data.savingsTransactions) {
                    const transactionData = {
                        ...transaction,
                        ngayGiaoDich: new Date(transaction.ngayGiaoDich),
                        importedAt: new Date()
                    };

                    // Add transaction to Firestore
                    await addDoc(collection(db, `users/${userId}/savingsTransactions`), transactionData);
                }
            }

            // Save import history
            this.saveImportHistory(userId, data);

        } catch (error) {
            console.error('Error importing data:', error);
            throw error;
        }
    },

    // Download file JSON
    downloadJSON(data, filename = 'debt-backup') {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    },

    // Auto backup setup
    setupAutoBackup(userId, exportFunction) {
        const lastBackup = localStorage.getItem('lastAutoBackup');
        const today = new Date().toDateString();

        // Set up daily backup check
        const checkBackup = () => {
            const currentDate = new Date().toDateString();
            const lastBackupDate = localStorage.getItem('lastAutoBackup');

            if (lastBackupDate !== currentDate) {
                exportFunction().then(data => {
                    // Save to localStorage
                    localStorage.setItem('autoBackup', JSON.stringify(data));
                    localStorage.setItem('lastAutoBackup', currentDate);

                    // Save backup history
                    this.saveBackupHistory(data);

                    console.log('Auto backup completed for', currentDate);
                }).catch(error => {
                    console.error('Auto backup failed:', error);
                });
            }
        };

        // Check immediately
        checkBackup();

        // Set up daily check (every 24 hours)
        setInterval(checkBackup, 24 * 60 * 60 * 1000);

        localStorage.setItem('autoBackupEnabled', 'true');
    },

    // Get backup history
    getBackupHistory() {
        const history = JSON.parse(localStorage.getItem('backupHistory') || '[]');
        const lastBackup = localStorage.getItem('lastAutoBackup');
        const hasBackup = localStorage.getItem('autoBackup') !== null;

        return {
            history: history.slice(-10), // Last 10 backups
            lastBackup,
            hasBackup
        };
    },

    // Save backup history (cập nhật để bao gồm tiết kiệm)
    saveBackupHistory(data) {
        const history = JSON.parse(localStorage.getItem('backupHistory') || '[]');
        const backupInfo = {
            id: Date.now().toString(),
            date: new Date().toLocaleString('vi-VN'),
            size: `${Math.round(JSON.stringify(data).length / 1024)} KB`,
            debtsCount: data.debts?.length || 0,
            paymentsCount: data.payments?.length || 0,
            savingsCount: data.savings?.length || 0,
            savingsTransactionsCount: data.savingsTransactions?.length || 0,
            version: data.version || '1.0'
        };

        history.push(backupInfo);

        // Keep only last 20 backups
        if (history.length > 20) {
            history.splice(0, history.length - 20);
        }

        localStorage.setItem('backupHistory', JSON.stringify(history));
    },

    // Save import history (cập nhật để bao gồm tiết kiệm)
    saveImportHistory(userId, data) {
        const history = JSON.parse(localStorage.getItem('importHistory') || '[]');
        const importInfo = {
            id: Date.now().toString(),
            date: new Date().toLocaleString('vi-VN'),
            userId,
            debtsCount: data.debts?.length || 0,
            paymentsCount: data.payments?.length || 0,
            savingsCount: data.savings?.length || 0,
            savingsTransactionsCount: data.savingsTransactions?.length || 0,
            source: data.exportDate ? 'exported_data' : 'manual_data',
            version: data.version || '1.0'
        };

        history.push(importInfo);

        // Keep only last 10 imports
        if (history.length > 10) {
            history.splice(0, history.length - 10);
        }

        localStorage.setItem('importHistory', JSON.stringify(history));
    },

    // Download specific backup
    downloadBackup(backupId) {
        if (!backupId) {
            // Download latest auto backup
            const backup = localStorage.getItem('autoBackup');
            if (backup) {
                const data = JSON.parse(backup);
                this.downloadJSON(data, 'auto-backup');
            }
        } else {
            // Download specific backup by ID
            const history = this.getBackupHistory().history;
            const backup = history.find(b => b.id === backupId);
            if (backup) {
                // In a real app, you'd fetch the backup data by ID
                // For now, just download the auto backup
                this.downloadBackup();
            }
        }
    },

    // Tạo template mẫu (cập nhật để bao gồm tiết kiệm)
    generateTemplate() {
        return {
            debts: [
                {
                    ngayVay: new Date().toISOString(),
                    ngayTra: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                    nguoiVay: "Nguyễn Văn A",
                    soTien: 1000000,
                    mucDich: "Vay tiền mua xe",
                    soTienDaTra: 0,
                    daTra: false
                },
                {
                    ngayVay: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                    ngayTra: new Date(Date.now() + 23 * 24 * 60 * 60 * 1000).toISOString(),
                    nguoiVay: "Trần Thị B",
                    soTien: 500000,
                    mucDich: "Vay tiền khẩn cấp",
                    soTienDaTra: 200000,
                    daTra: false
                }
            ],
            payments: [
                {
                    debtId: "debt_id_reference",
                    nguoiVay: "Trần Thị B",
                    soTienTra: 200000,
                    ngayTra: new Date().toISOString(),
                    ghiChu: "Trả một phần"
                }
            ],
            savings: [
                {
                    tenGoiTietKiem: "Tiết kiệm định kỳ 12 tháng",
                    nganHang: "Vietcombank",
                    soTienGoc: 10000000,
                    laiSuat: 6.5,
                    kyHan: 12,
                    kyHanText: "12 tháng",
                    loaiLai: "monthly",
                    ngayTao: new Date().toISOString(),
                    ngayDaoHan: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
                    trangThai: "active",
                    ghiChu: "Tiết kiệm cho mục tiêu mua nhà"
                }
            ],
            savingsTransactions: [
                {
                    savingsId: "savings_id_reference",
                    loaiGiaoDich: "deposit",
                    soTien: 1000000,
                    soDuSauGiaoDich: 11000000,
                    ngayGiaoDich: new Date().toISOString(),
                    lyDo: "Nạp thêm tiền"
                }
            ],
            exportDate: new Date().toISOString(),
            userId: "template",
            version: "2.0"
        };
    }
};
